import { useCallback, useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding, useEditMode } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  classifyPhoto,
  GalleryLightbox,
  type GalleryImage,
} from './_galleryShared';

/**
 * Single staggered photo gallery that combines apartment + community shots
 * from `photo_gallery` into one asymmetric mosaic. Inspired by
 * lakehausapartmenthomes.com/gallery — tiles cycle through a repeating
 * 8-image pattern on desktop that alternates a 2x2 "hero" tile between the
 * left and right sides of the grid, with mixed 1x1 and 2x1 tiles filling
 * around it. Clicking any tile opens the shared lightbox.
 *
 * Because every photo in `photo_gallery` is delivered as 16:9, a pure
 * column-flow masonry layout collapses to a uniform grid — we instead use
 * CSS Grid with explicit row/column spans to force the staggered look.
 *
 * Forked from the base oasis theme alongside the local `./_galleryShared`
 * fork: mobile viewports now render the same staggered grid (2 columns)
 * instead of a single-image swipe carousel, per customer request. The
 * expanded lightbox also keeps a fixed image frame so paging between
 * photos of differing aspect ratios stays visually stable.
 */
export function GalleryStaggered() {
  const images = useCollectionBinding<GalleryImage>('gallerystaggered.images', {
    collection: 'photo_gallery',
  });

  // Keep apartment + community shots, drop unclassified marketing/other
  // photos so the grid stays cohesive. If everything is "other" (e.g. a
  // property whose titles aren't tagged), fall back to all photos so the
  // gallery never collapses to empty.
  const photos = useMemo(() => {
    const list = images && images.length > 0 ? images : [];
    const tagged = list.filter((img) => {
      const kind = classifyPhoto(img);
      return kind === 'apartment' || kind === 'community';
    });
    return tagged.length > 0 ? tagged : list;
  }, [images]);

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // In the inline editor, a tile click should select the gallery section for
  // editing — not launch the viewer lightbox. Opening the full-screen overlay
  // mid-edit hides the section the editor is trying to select and obscures the
  // canvas, so we suppress it whenever edit mode is active. `useEditMode()`
  // returns null outside an editor context (deployed sites), where the lightbox
  // behaves exactly as before.
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Open the lightbox only outside edit mode. The handler is a no-op in edit
  // mode so the click bubbles up to the editor's section selection instead.
  const openLightbox = useCallback(
    (index: number) => {
      if (isEditMode) return;
      setSelectedIndex(index);
    },
    [isEditMode],
  );

  return (
    <section className="bg-background">
      <div className="gallery-staggered__wrap">
        {photos.length === 0 ? (
          <p className="gallery-staggered__empty">
            Gallery images will be displayed here.
          </p>
        ) : (
          /* Staggered grid rendered at every breakpoint — 2 columns on
             mobile, escalating to the 4-column mosaic at desktop. */
          <div className="gallery-staggered__grid">
            {photos.map((img, index) => (
              <button
                key={`${img.image || index}-${index}`}
                type="button"
                className="gallery-staggered__item"
                onClick={() => openLightbox(index)}
                aria-label={`Open photo ${index + 1} in lightbox`}
              >
                <VibesitesImage
                  url={img.image || img.original_url || ''}
                  alt={img.alt_text || img.title || ''}
                  className="gallery-staggered__img"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Never render the open viewer overlay in edit mode — passing a null
          index keeps it fully inert (no keyboard nav, no body-scroll lock) so
          it can't cover the canvas the editor is selecting. */}
      <GalleryLightbox
        images={photos}
        selectedIndex={isEditMode ? null : selectedIndex}
        onClose={() => setSelectedIndex(null)}
        onChange={setSelectedIndex}
      />

      <style>{`
        .gallery-staggered__wrap {
          width: 96vw;
          max-width: 96vw;
          margin-left: auto;
          margin-right: auto;
          padding-top: 1rem;
          padding-bottom: 1rem;
        }

        @media (min-width: 768px) {
          .gallery-staggered__wrap {
            padding-top: 1.5rem;
            padding-bottom: 1.5rem;
          }
        }

        .gallery-staggered__empty {
          font-family: var(--vibe-font-body);
          font-size: 1.125rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          padding: 3rem 0;
        }

        /* Asymmetric CSS-Grid mosaic. Tiles use explicit col/row spans to
           produce the staggered pattern; grid-auto-flow:dense lets later
           1x1 tiles back-fill any holes when the photo count isn't an exact
           multiple of the 8-tile block.

           Mobile default: 2-column grid with a simple 4-tile stagger
           pattern (every 4th tile is a tall 1x2). Row height is generous
           so 16:9 photos still read as full images rather than thin
           slivers on phone widths. */
        .gallery-staggered__grid {
          display: grid;
          gap: 0.25rem;
          grid-template-columns: repeat(2, 1fr);
          grid-auto-rows: 45vw;
          grid-auto-flow: dense;
        }
        .gallery-staggered__item:nth-child(4n + 1) {
          grid-row: span 2;
        }

        /* Tablet: same 2-column stagger, but tighter row height now that
           the viewport is wider. */
        @media (min-width: 640px) {
          .gallery-staggered__grid {
            grid-auto-rows: 30vw;
          }
        }

        /* Desktop: 4-column mosaic. Repeating 8-tile pattern:
           [ 1 1 2 3 ]
           [ 1 1 4 4 ]
           [ 5 6 7 7 ]
           [ 8 8 7 7 ]
           — big 2x2 tile alternates between the left and right halves. */
        @media (min-width: 1024px) {
          .gallery-staggered__grid {
            grid-template-columns: repeat(4, 1fr);
            grid-auto-rows: 16vw;
          }
          /* Reset the tablet rule first so it doesn't leak into desktop. */
          .gallery-staggered__item:nth-child(4n + 1) {
            grid-row: auto;
          }
          /* 2x2 hero tiles — positions 1 and 7 in the 8-tile cycle. */
          .gallery-staggered__item:nth-child(8n + 1),
          .gallery-staggered__item:nth-child(8n + 7) {
            grid-column: span 2;
            grid-row: span 2;
          }
          /* 2x1 wide tiles — positions 4 and 8 in the 8-tile cycle. */
          .gallery-staggered__item:nth-child(8n + 4),
          .gallery-staggered__item:nth-child(8n + 8) {
            grid-column: span 2;
            grid-row: span 1;
          }
        }

        @media (min-width: 1400px) {
          .gallery-staggered__grid {
            grid-auto-rows: 15vw;
          }
        }

        .gallery-staggered__item {
          display: block;
          width: 100%;
          height: 100%;
          padding: 0;
          margin: 0;
          border: none;
          background: none;
          cursor: pointer;
          overflow: hidden;
          animation: gallery-staggered-fade-in 0.4s ease both;
          transition: box-shadow 0.3s ease;
        }

        .gallery-staggered__item:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
        }

        .gallery-staggered__item:focus-visible {
          outline: 2px solid var(--vibe-color-accent);
          outline-offset: 2px;
        }

        .gallery-staggered__img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-staggered__item:hover .gallery-staggered__img {
          transform: scale(1.04);
        }

        @keyframes gallery-staggered-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .gallery-staggered__item,
          .gallery-staggered__img {
            animation: none;
            transition: none;
          }
          .gallery-staggered__item:hover .gallery-staggered__img {
            transform: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'GalleryStaggered',
  displayName: 'Staggered Photo Gallery',
  // No editable content tokens — every photo is rendered through the
  // photo_gallery collection binding. The field is still required by
  // ComponentContentSchema, and the editor iterates it with
  // Object.entries(schema.tokens) when the section is selected, so it must
  // be a real object rather than undefined. Dropping it (as this fork
  // originally did) makes the editor throw "Cannot convert undefined or null
  // to object" on every click into the gallery in edit mode.
  tokens: {},
  collectionBindings: {
    'gallerystaggered.images': {
      label: 'Gallery Photos',
      collection: 'photo_gallery',
      description:
        'Pulled from the property photo gallery. Apartment + community shots are shown together in a single asymmetric staggered mosaic.',
    },
  },
};

(GalleryStaggered as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GalleryStaggered;
